import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  name,
  placeholder = "Seçin veya yazın...",
  label,
  required = false,
  disabled = false,
  displayKey = "label",
  valueKey = "value",
  showIcon = true,
  iconType = "user",
  className = "",
  renderOption = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Seçili değerin label'ını bul
  const selectedOption = options.find(opt => 
    typeof opt === 'object' ? opt[valueKey] === value : opt === value
  );

  const getDisplayValue = () => {
    if (!selectedOption) return '';
    if (typeof selectedOption === 'object') {
      return selectedOption[displayKey];
    }
    return selectedOption;
  };

  // Filtrelenmiş seçenekler
  const filteredOptions = options.filter(option => {
    const searchValue = searchTerm.toLowerCase();
    if (typeof option === 'object') {
      // Nesne içindeki tüm string değerlerde ara
      return Object.values(option).some(val => 
        String(val).toLowerCase().includes(searchValue)
      );
    }
    return String(option).toLowerCase().includes(searchValue);
  });

  // Dışarı tıklamayı algıla
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Klavye navigasyonu
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelect = (option) => {
    const newValue = typeof option === 'object' ? option[valueKey] : option;
    onChange({ target: { value: newValue, name: name || label } });
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const getIcon = () => {
    switch (iconType) {
      case 'user':
        return (
          <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'teacher':
        return (
          <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'calendar':
        return (
          <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const defaultRenderOption = (option, isHighlighted) => {
    if (typeof option === 'object') {
      // Öğretmen veya öğrenci için özel görünüm
      if (option.adi || option.name) {
        const name = option.adi || option.name;
        const phone = option.telefon || option.phone || option.parent_phone;
        const email = option.e_posta || option.email;
        const status = option.status;
        
        return (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                isHighlighted ? 'bg-blue-600' : 'bg-gradient-to-br from-blue-400 to-blue-600'
              }`}>
                {name[0].toUpperCase()}
              </div>
              <div>
                <div className="font-medium text-gray-900">{name}</div>
                <div className="text-xs text-gray-500">
                  {phone && <span>{phone}</span>}
                  {email && phone && <span className="mx-1">•</span>}
                  {email && <span>{email}</span>}
                </div>
              </div>
            </div>
            {status !== undefined && (
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                status === 1 || status === '1' ? 'bg-green-100 text-green-700' :
                status === 2 || status === '2' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                {status === 1 || status === '1' ? 'Aktif' :
                 status === 2 || status === '2' ? 'Beklemede' : 'Pasif'}
              </span>
            )}
          </div>
        );
      }
      return <span>{option[displayKey]}</span>;
    }
    return <span>{option}</span>;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {showIcon && getIcon()}
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`w-full px-4 py-2.5 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          placeholder={placeholder}
          value={isOpen ? searchTerm : getDisplayValue()}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          required={required}
        />
        
        {/* Dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 max-h-60 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isHighlighted = index === highlightedIndex;
              const optionValue = typeof option === 'object' ? option[valueKey] : option;
              const isSelected = optionValue === value;
              
              return (
                <div
                  key={optionValue}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    isHighlighted ? 'bg-blue-50' : 
                    isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {renderOption ? renderOption(option, isHighlighted) : defaultRenderOption(option, isHighlighted)}
                  {isSelected && (
                    <svg className="w-4 h-4 text-blue-600 float-right" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              );
            })
          ) : (
            <div className="px-4 py-3 text-gray-500 text-center">
              <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-sm">Sonuç bulunamadı</p>
              <p className="text-xs text-gray-400 mt-1">Farklı bir arama terimi deneyin</p>
            </div>
          )}
          
          {/* Toplam sonuç sayısı */}
          {filteredOptions.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
              <p className="text-xs text-gray-500">
                {filteredOptions.length} sonuç bulundu
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
